import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { AnchorProps } from "@headlessui/react/dist/internal/floating";
import classNames from "classnames";
import clsx from "clsx";
import { debounce } from "lodash";
import { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import { PRIMARY_COLOR } from "../../utils/constants";
import { Icon } from "../icon/icon";
import { LoadingOval } from "../loaders/loaders";

const handleSearch = (searchFunction: (val: any) => void, query: any) => {
  searchFunction(query);
};

const handleDebouncedSearch = debounce(handleSearch, 800);

const ComboBox = ({
  children,
  menuItems,
  className = "",
  menuClassName = "",
  menuItemsClassName = "",
  position = "bottom",
  onSearch = () => {},
  onMenuClose = () => {},
  onSelect = () => {},
  loading = false,
}: {
  children: ReactNode;
  menuItems?: {
    name: ReactNode;
    value?: string | object;
    onClick?: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  }[];
  className?: string;
  menuClassName?: string;
  menuItemsClassName?: string;
  onSearch?: (val: string) => void;
  onMenuClose?: () => void;
  onSelect?: (val?: any) => void;
  loading?: boolean;
  position?: AnchorProps;
}) => {
  const [width, setWidth] = useState(0);
  const [searchParams, setSearchParams] = useState<string>("");

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      setWidth(containerRef.current.offsetWidth);
    }
  }, [containerRef?.current?.offsetWidth]);

  useEffect(() => {
    handleDebouncedSearch(onSearch, searchParams);
    // eslint-disable-next-line
  }, [searchParams]);

  return (
    <Combobox
      as="div"
      className={`relative inline-block text-left !outline-none focus-within:!outline-none focus:!outline-none ${className}`}
      onChange={(val) => {
        if (val) {
          onSelect(val);
        }
      }}
      onClose={() => {
        setSearchParams("");
        onMenuClose();
      }}
    >
      <div
        className="w-full outline-none focus-within:outline-none focus:outline-none"
        ref={containerRef}
      >
        <ComboboxInput
          as={Fragment}
          autoFocus={false}
          onChange={(e) => {
            setSearchParams(e.target.value);
          }}
        >
          {children}
        </ComboboxInput>
      </div>

      <ComboboxOptions
        className={classNames(
          `min-w-ma relative !z-[900] flex w-full flex-col overflow-x-hidden overflow-y-scroll`,
          "rounded-md border bg-white",
          "[--anchor-gap:16px]",
          "outline-none focus-within:outline-none focus:outline-none",
          menuClassName,
        )}
        transition
        anchor={position}
        style={{
          width: width,
          maxWidth: `${width}px`,
        }}
        modal
      >
        {!loading &&
          menuItems?.map((item, index) => {
            const key = `menu-item-${index}`;
            return (
              <ComboboxOption
                key={key}
                as="button"
                type="button"
                value={item.value}
                className={clsx(
                  "w-full border-b bg-inherit p-3 text-left text-sm font-medium text-primary transition-all duration-200 last:border-b-0 hover:bg-grey-50",
                  menuItemsClassName,
                )}
              >
                {item.name}
              </ComboboxOption>
            );
          })}

        {loading ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-[10px] py-10">
            <LoadingOval
              loaderWidth="20"
              loaderHeight="20"
              color={PRIMARY_COLOR}
            />
          </div>
        ) : undefined}

        {menuItems && menuItems.length === 0 && !loading ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-[10px] py-10">
            <Icon
              svg="search-x"
              width={44}
              height={44}
              className="rounded-full bg-grey-100 p-3 text-grey-600"
            />

            <p className="text-sm">No Result Found</p>
          </div>
        ) : undefined}
      </ComboboxOptions>
    </Combobox>
  );
};

export default ComboBox;
